import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const CHECK_INTERVAL_MS = 3 * 60 * 1000; // every 3 minutes

// Returns the hash portion of the main JS bundle currently loaded in the DOM,
// e.g. "/static/js/main.4f8a91bc.chunk.js" → "4f8a91bc"
function getCurrentBundleHash() {
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  const main = scripts.find(s => s.src.includes('/static/js/main'));
  return main ? main.src : null;
}

// Fetches asset-manifest.json and returns the main JS src string
async function fetchLatestBundleSrc() {
  const res = await fetch('/asset-manifest.json?_=' + Date.now(), {
    cache: 'no-store',
  });
  if (!res.ok) return null;
  const data = await res.json();
  // CRA manifest key is "main.js" in files
  const src = data?.files?.['main.js'];
  return src || null;
}

export default function VersionChecker() {
  const location = useLocation();
  const currentSrcRef = useRef(getCurrentBundleHash());

  const check = async (path) => {
    // Never interrupt someone mid-test
    if (path && path.startsWith('/assessment/test')) return;
    try {
      const latestSrc = await fetchLatestBundleSrc();
      if (!latestSrc) return;
      const current = currentSrcRef.current;
      // current is a full URL, latestSrc is a path — compare the filename hash part
      if (current && !current.includes(latestSrc.replace(/^\//, ''))) {
        window.location.reload(true);
      }
    } catch {
      // network error — silently ignore, try next time
    }
  };

  useEffect(() => {
    // Periodic check
    const interval = setInterval(() => check(window.location.pathname), CHECK_INTERVAL_MS);

    // Check when user switches back to this tab
    const onVisible = () => {
      if (document.visibilityState === 'visible') check(window.location.pathname);
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Also check on every route change (but not on test page)
  useEffect(() => {
    check(location.pathname);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return null; // renders nothing
}
